/** @jsxImportSource theme-ui */
import { Formik } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import { Spinner, Text } from 'theme-ui';
import { toast } from 'react-toastify';
import {
    useGetBillingCountriesQuery,
    useGetBillingInformationQuery,
    useGetBillingLicensePlansQuery,
    useGetBillingMethodsQuery,
    usePutBillingInformationMutation,
} from '@/store/apiService';
import Error from '@/components/Error';
import BillingInformationForm from './BillingInformationForm';
import { mapServerErrors, toFormValues, toPayload, validateBillingInformation } from './validation';

const EditBillingInformation: React.FC = () => {
    const { companyId } = useParams<{ companyId: string }>();
    const navigate = useNavigate();

    const billingQuery = useGetBillingInformationQuery(
        { CompanyId: companyId! },
        { skip: !companyId }
    );
    const methodsQuery = useGetBillingMethodsQuery();
    const countriesQuery = useGetBillingCountriesQuery();
    const plansQuery = useGetBillingLicensePlansQuery();
    const [saveBillingInformation, saveState] = usePutBillingInformationMutation();

    if (billingQuery.isLoading) {
        return <Spinner size={16} />;
    }

    if (billingQuery.error) {
        const status = (billingQuery.error as { status?: number }).status;

        return status === 404 ? (
            <Text>This company has no billing information yet.</Text>
        ) : (
            <Error error={billingQuery.error as any} text="Could not load billing information" />
        );
    }

    if (!billingQuery.data || !companyId) {
        return <Text>No billing information found</Text>;
    }

    const billingMethods = methodsQuery.data?.Results ?? [];
    const currentLicensePlanId = billingQuery.data.LicensePlanId;
    const isLoadingOptions =
        methodsQuery.isLoading || countriesQuery.isLoading || plansQuery.isLoading;

    return (
        <Formik
            enableReinitialize
            initialValues={toFormValues(billingQuery.data)}
            validate={(values) =>
                validateBillingInformation(values, {
                    // Only enforce the method/country pairing once the methods have loaded
                    billingMethods: billingMethods.length ? billingMethods : undefined,
                    currentLicensePlanId,
                })
            }
            onSubmit={(values, { setSubmitting, setErrors }) => {
                saveBillingInformation(toPayload(companyId, values))
                    .unwrap()
                    .then(() => toast.success('Billing information saved', { autoClose: 3000 }))
                    .catch((err) => {
                        // Rules only the API knows about land next to the right field
                        setErrors(mapServerErrors(err));
                        toast.error('Could not save billing information', { autoClose: 5000 });
                    })
                    .finally(() => setSubmitting(false));
            }}
        >
            {(formProps) => (
                <BillingInformationForm
                    {...formProps}
                    countries={countriesQuery.data?.Results ?? []}
                    billingMethods={billingMethods}
                    licensePlans={plansQuery.data?.Results ?? []}
                    currentLicensePlanId={currentLicensePlanId}
                    isLoadingOptions={isLoadingOptions}
                    saveError={saveState.error}
                    onCancel={() => navigate(`/clients/${companyId}`)}
                />
            )}
        </Formik>
    );
};

export default EditBillingInformation;
